/**
 * CmtWrapper — the stable interface to the Classification Management Tool.
 *
 * This component is the only thing the rest of the app touches. It renders a
 * trigger button showing the current marking and, on click, opens either:
 *
 *   - The REAL CMT popup, when `window.__openCMT` is present (production network).
 *   - The CmtMock dialog, when the real CMT is not available (dev / off-network).
 *
 * ## Integrating the real CMT
 * The real CMT JS should expose a global function with this signature:
 *
 *   window.__openCMT(
 *     current: { classification: string; disseminationControls: string[] },
 *     callback: (result: { classification: string; disseminationControls: string[] } | null) => void
 *   ): void
 *
 * A null callback argument means the user cancelled — the value is left unchanged.
 * When the real CMT is present, CmtMock is never imported or rendered.
 */

import { useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { formatCmtResult } from './types'
import type { CmtResult } from './types'

// Lazily import the mock so it tree-shakes cleanly in production builds
// (the real network will have window.__openCMT; the import won't matter either way,
// but keeping it lazy is good hygiene).
import { CmtMock } from './CmtMock'

// ── Real CMT bridge ─────────────────────────────────────────────────────────

declare global {
  interface Window {
    /** Injected by the real CMT JavaScript on the production network. */
    __openCMT?: (
      current: CmtResult,
      callback: (result: CmtResult | null) => void
    ) => void
  }
}

function hasRealCmt(): boolean {
  return typeof window !== 'undefined' && typeof window.__openCMT === 'function'
}

// ── Component ────────────────────────────────────────────────────────────────

type Props = {
  /** Current classification value. Undefined = not yet set (shows "Set marking"). */
  value: CmtResult | undefined
  /** Called when the user confirms a selection (real or mock). */
  onChange: (result: CmtResult) => void
  /** Optional additional classes on the trigger button. */
  className?: string
  /** If true, renders the button in a compact/inline style. */
  compact?: boolean
}

export function CmtWrapper({ value, onChange, className = '', compact = false }: Props) {
  const [mockOpen, setMockOpen] = useState(false)

  const handleClick = () => {
    if (hasRealCmt()) {
      window.__openCMT!(value ?? { classification: 'U', disseminationControls: [] }, (result) => {
        if (result !== null) onChange(result)
      })
    } else {
      setMockOpen(true)
    }
  }

  return (
    <>
      {compact ? (
        <div className="space-y-1">
          <button
            type="button"
            onClick={handleClick}
            className={[
              'inline-flex items-center gap-1.5 rounded border px-2 py-0.5 text-[10px] font-semibold transition-colors',
              'border-line bg-surface text-fg-secondary hover:bg-fill hover:text-fg',
              className,
            ].join(' ')}
          >
            <ShieldCheck size={11} aria-hidden="true" />
            Classify
          </button>
          {value && (
            <p className="font-mono text-[10px] text-fg-secondary">{formatCmtResult(value)}</p>
          )}
        </div>
      ) : (
        <>
          <button
            type="button"
            onClick={handleClick}
            className={[
              'inline-flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs font-semibold transition-colors',
              'border-line bg-surface text-fg-secondary hover:bg-fill hover:text-fg',
              className,
            ].join(' ')}
          >
            <ShieldCheck size={13} aria-hidden="true" />
            Classify
          </button>
          {value && (
            <p className="mt-1.5 font-mono text-[11px] text-fg-secondary">{formatCmtResult(value)}</p>
          )}
        </>
      )}

      {/* Only renders when real CMT is absent */}
      {!hasRealCmt() && (
        <CmtMock
          open={mockOpen}
          onOpenChange={setMockOpen}
          value={value}
          onSelect={onChange}
        />
      )}
    </>
  )
}
